import React from 'react';
import { ShieldAlert } from 'lucide-react';
import appConfig from '../../config/appConfig';

// Baseline legal text. This is a STARTING TEMPLATE, not legal advice — it renders
// only until the admin pastes a lawyer-reviewed version into platform settings.
// Kept protective by default: marketplace/intermediary disclaimer + DPDP basics.
const L = appConfig.legal || {};

// Shown to admins/owners (logged in) so they know to replace it. Hidden from the
// public, who should just see the terms as written.
export function TemplateNotice({ show }) {
  if (!show) return null;
  return (
    <div className="flex items-start gap-2 mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
      <p className="text-sm text-amber-800">
        <span className="font-semibold">This is a starter template, not final legal text.</span>{' '}
        Have it reviewed by a lawyer and paste the approved version in Settings → Platform.
        Until then, this baseline is shown to visitors.
      </p>
    </div>
  );
}

// Reusable grievance / data-protection contact block, required by India's IT
// intermediary rules and the DPDP Act.
export function GrievanceBlock() {
  return (
    <div className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-5">
      <h3 className="text-base font-bold text-gray-900 mb-2">Grievance &amp; Data Protection Officer</h3>
      <p className="text-sm text-gray-600 mb-3">
        For complaints about content, listings, or your personal data, contact:
      </p>
      <div className="text-sm text-gray-800 space-y-1">
        <p><span className="text-gray-500">Business:</span> {appConfig.name}{L.businessType ? ` — ${L.businessType}` : ''}</p>
        {L.udyamNumber && <p><span className="text-gray-500">Udyam Reg. No.:</span> {L.udyamNumber}</p>}
        <p><span className="text-gray-500">Name:</span> {L.grievanceOfficer}</p>
        <p><span className="text-gray-500">Email:</span> <a className="text-blue-600" href={`mailto:${L.grievanceEmail}`}>{L.grievanceEmail}</a></p>
        {L.grievancePhone && <p><span className="text-gray-500">Phone:</span> {L.grievancePhone}</p>}
        <p><span className="text-gray-500">Address:</span> {L.address}</p>
      </div>
      <p className="text-xs text-gray-400 mt-3">
        We acknowledge complaints within 24 hours and aim to resolve them within 15 days.
      </p>
    </div>
  );
}

const P = 'text-gray-700 leading-relaxed mb-4';
const H = 'text-lg font-bold text-gray-900 mt-6 mb-2';

export function TermsBaseline() {
  return (
    <div>
      <p className="text-sm text-gray-400 mb-6">Last updated: {L.lastUpdated}</p>
      <p className={P}>
        These terms govern your use of {appConfig.name}, a platform operated by {L.companyName}
        {L.businessType ? `, a ${L.businessType}` : ''}{L.udyamNumber ? ` (${L.udyamNumber})` : ''}, that
        connects vehicle owners and agencies with customers who wish to rent or buy vehicles.
      </p>

      <h2 className={H}>1. We are a marketplace, not the seller</h2>
      <p className={P}>
        {appConfig.name} is an intermediary. Vehicles, prices, availability, condition and all
        listing information are provided by independent owners and agencies, not by us. We do not
        own, inspect, or guarantee any vehicle listed on the platform. Any rental or sale contract
        is directly between you and the owner.
      </p>

      <h2 className={H}>2. Verify before you pay</h2>
      <p className={P}>
        Listing details — including registration year, kilometres driven, ownership, insurance,
        permit, fitness and loan status — are entered by the seller and may be inaccurate. You are
        responsible for independently verifying the vehicle, its documents (RC, insurance, permit,
        NOC) and its legal status before making any payment or entering any agreement.
      </p>

      <h2 className={H}>3. Owner responsibilities</h2>
      <p className={P}>
        Owners and agencies confirm that their vehicles hold valid registration, insurance and,
        where applicable, commercial permits and fitness certificates, and that they are legally
        entitled to rent or sell each vehicle. Owners are solely responsible for the accuracy of
        their listings and the condition of their vehicles.
      </p>

      <h2 className={H}>4. Payments</h2>
      <p className={P}>
        Subscription payments are collected manually and activated by our team. We are not a party
        to, and take no responsibility for, payments made directly between customers and owners.
      </p>

      <h2 className={H}>5. Limitation of liability</h2>
      <p className={P}>
        To the maximum extent permitted by law, {L.companyName} is not liable for any loss arising
        from a transaction between a customer and an owner, from inaccurate listing information, or
        from the condition, legality or documentation of any vehicle.
      </p>

      <h2 className={H}>6. Prohibited use</h2>
      <p className={P}>
        You may not post false listings, impersonate others, or use the platform for any unlawful
        purpose. We may remove listings and suspend accounts that breach these terms.
      </p>

      <GrievanceBlock />
    </div>
  );
}

export function PrivacyBaseline() {
  return (
    <div>
      <p className="text-sm text-gray-400 mb-6">Last updated: {L.lastUpdated}</p>
      <p className={P}>
        This policy explains how {appConfig.name} ({L.companyName}) collects and uses your personal
        data, in line with India's Digital Personal Data Protection Act, 2023.
      </p>

      <h2 className={H}>1. What we collect</h2>
      <p className={P}>
        Name, phone number and email; vehicle and document details you upload (such as RC,
        insurance and licence images); booking and enquiry details; and basic usage information.
      </p>

      <h2 className={H}>2. Why we collect it</h2>
      <p className={P}>
        To operate the platform — create your account, show listings, connect customers with
        owners, process bookings and enquiries, verify documents, and provide support. We collect
        it with your consent and use it only for these stated purposes.
      </p>

      <h2 className={H}>3. Sharing</h2>
      <p className={P}>
        When you make a booking or enquiry, relevant contact details are shared with the vehicle
        owner so they can respond. We do not sell your personal data. We use trusted service
        providers (for example, cloud image storage) under appropriate safeguards.
      </p>

      <h2 className={H}>4. Your rights</h2>
      <p className={P}>
        You may ask us to access, correct or delete your personal data, and withdraw consent, by
        contacting the officer below. We will act on valid requests within a reasonable time.
      </p>

      <h2 className={H}>5. Security &amp; retention</h2>
      <p className={P}>
        We take reasonable measures to protect your data and retain it only as long as needed for
        the purposes above or as required by law.
      </p>

      <GrievanceBlock />
    </div>
  );
}
